/** Run history sheet + templates gallery for the Automation Engine. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import { RUN_CLS, fmtWhen, condText } from "./automationUtils";

function RunItem({ r }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-slate-200 bg-white" data-testid={`run-${r.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex flex-wrap items-center gap-2 px-3 py-2 text-left text-[12px]">
        {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
        <span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase ${RUN_CLS[r.status] || RUN_CLS.no_match}`}>{r.status.replace("_", " ")}</span>
        <span className="font-mono text-slate-700">{fmtWhen(r.started_at)}</span>
        <span className="text-slate-500">scanned {r.scanned} · matched {r.matched} · fired <b className="text-slate-800">{r.fired}</b>{r.skipped_cooldown ? ` · ${r.skipped_cooldown} cooldown` : ""}</span>
        <span className="ml-auto font-mono text-[10px] text-slate-400">{r.triggered_by} · {r.duration_ms}ms</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-3 py-2 space-y-1 text-[11px]">
          {r.errors?.map((e, i) => <div key={i} className="text-rose-600">{e}</div>)}
          {(r.samples || []).map((s, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-slate-800">{s.title}</span>
              <span className="text-slate-500">{s.customer || ""}</span>
              <span className="ml-auto text-slate-500">{(s.results || []).map((x) => `${x.type}: ${x.detail}`).join(" · ")}</span>
            </div>
          ))}
          {!r.samples?.length && !r.errors?.length && <div className="text-slate-400">Nothing fired in this run.</div>}
        </div>
      )}
    </div>
  );
}

export function RunHistory({ rule, onClose }) {
  const [runs, setRuns] = useState(null);
  useEffect(() => {
    if (!rule) return;
    setRuns(null);
    api.get("/automation/runs", { params: rule.id === "*" ? { limit: 100 } : { rule_id: rule.id, limit: 100 } }).then(({ data }) => setRuns(data.items)).catch(() => setRuns([]));
  }, [rule]);
  return (
    <Sheet open={!!rule} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="run-history">
        <SheetHeader><SheetTitle className="font-heading">{rule?.id === "*" ? "All runs" : `Run history · ${rule?.name}`}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-1.5">
          {!runs && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>}
          {runs?.length === 0 && <div className="text-sm text-slate-500" data-testid="run-history-empty">No runs yet. Enable the automation or click Run now.</div>}
          {runs?.map((r) => <RunItem key={r.id} r={r} />)}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function TemplatesGallery({ open, onClose, cat, onCreated, canManage }) {
  const [busy, setBusy] = useState(null);
  const use = async (t) => {
    setBusy(t.key);
    try { const { data } = await api.post(`/automation/rules/from-template/${t.key}`); toast.success("Automation created (disabled) — review and enable"); onCreated(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(null); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="templates-gallery">
        <DialogHeader><DialogTitle className="font-heading text-lg flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" />Automation templates</DialogTitle></DialogHeader>
        <p className="text-[12px] text-slate-500">Each template is created <b>disabled</b> so you can review thresholds and recipients before switching it on.</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {cat.templates.map((t) => {
            const trig = cat.triggers.find((x) => x.key === t.trigger.type);
            return (
              <div key={t.key} className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col gap-1.5" data-testid={`template-${t.key}`}>
                <div className="text-[13px] font-semibold text-slate-900">{t.name}</div>
                <div className="text-[11px] text-slate-500"><span className="font-mono uppercase text-[10px] text-sky-600">IF</span> {trig?.label}{t.conditions.length ? ` where ${t.conditions.map((c) => condText(c, cat.operators)).join(" and ")}` : ""}</div>
                <div className="text-[11px] text-slate-500"><span className="font-mono uppercase text-[10px] text-emerald-600">THEN</span> {t.actions.map((a) => cat.actions.find((x) => x.key === a.type)?.label).join(" + ")}</div>
                <Button size="sm" variant="outline" className="mt-auto self-start" disabled={!canManage || busy === t.key} onClick={() => use(t)} data-testid={`template-use-${t.key}`}>
                  {busy === t.key ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Use template
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
